import {createStyles, CSSObject} from '@mantine/core';
import React from 'react';

import ChartContent, {QueryingChartContent} from 'charts/chart-content/ChartContent';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import DataFrameTableWrapper from 'data-frames/wrapper/DataFrameTableWrapper';
import PersistedDataFrameTableWrapper from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import ScratchFilePaneContent, {QueryingScratchFilePaneContent} from 'pipelines/files/ScratchFilePaneContent';
import {ScratchFileContent} from 'pipelines/files/ScratchFileTypes';
import {PreviewChart} from 'pipelines/network/PipelineDAO';
import {PipelineStepRawData} from 'pipelines/PipelineTypes';
import ScratchPlateViewerPane from 'pipelines/plates/ScratchPlateViewerPane';
import Pane from 'ui-library/pane/Pane';
import {assertNever} from 'util/Types';

const ITEM_MARGIN = 12;
const ITEM_HEIGHT = 300;

const ITEM_BASE_STYLES: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  marginBottom: ITEM_MARGIN / 2,
  marginLeft: ITEM_MARGIN,
  marginRight: ITEM_MARGIN,
  marginTop: ITEM_MARGIN / 2,
};

const useStyles = createStyles({
  item: {
    ...ITEM_BASE_STYLES,
    height: ITEM_HEIGHT,
  },
  plateItem: {
    ...ITEM_BASE_STYLES,
    minHeight: ITEM_HEIGHT,
  },
  fullHeightItem: {
    ...ITEM_BASE_STYLES,
    flex: 1,
    minHeight: 0,
  },
});

export type PersistedRawDataItem<T extends PipelineStepDataKind = PipelineStepDataKind> =
  T extends PipelineStepDataKind ? {kind: T; preview: false; rawData: PipelineStepRawData<T>} : never;

export type PreviewRawDataItem =
  | {kind: PipelineStepDataKind.DATASET; preview: true; rawData: DataFrame}
  | {kind: PipelineStepDataKind.CHART; preview: true; rawData: PreviewChart}
  | {kind: PipelineStepDataKind.FILE; preview: true; rawData: ScratchFileContent};

/**
 * A single raw data to render, tagged by its kind and whether it is persisted or ephemeral.
 */
export type RawDataItem = PersistedRawDataItem | PreviewRawDataItem;

export interface SingleStepDataPreviewProps {
  item: RawDataItem;
  clientStateId: string;
  /** When true, the item fills the available height rather than using a fixed height. */
  fullHeight?: boolean;
}

/**
 * Render a persisted or preview pipeline raw data (chart, data frame, file, or plate) inside a
 * titled pane.
 *
 * @package
 */
export default function SingleStepDataPreview({
  item,
  clientStateId,
  fullHeight = false,
}: SingleStepDataPreviewProps): JSX.Element {
  const {classes} = useStyles();
  const itemClassName = fullHeight
    ? classes.fullHeightItem
    : item.kind === PipelineStepDataKind.PLATE
      ? classes.plateItem
      : classes.item;

  switch (item.kind) {
    case PipelineStepDataKind.CHART:
      return (
        <div className={itemClassName}>
          <Pane title={item.preview ? 'Chart' : item.rawData.name || 'Chart'}>
            {item.preview ? (
              <ChartContent chart={item.rawData} />
            ) : (
              <QueryingChartContent chartId={item.rawData.id} />
            )}
          </Pane>
        </div>
      );
    case PipelineStepDataKind.DATASET:
      return (
        <div className={itemClassName}>
          {item.preview ? (
            <DataFrameTableWrapper
              actionOrigin={ActionOrigin.ANALYSIS_TOOL}
              clientStateId={clientStateId}
              dataFrame={item.rawData}
              defaultTableMode={DataFrameTableMode.TEXT}
              renderIsExcludedColumn={false}
            />
          ) : (
            <PersistedDataFrameTableWrapper
              actionOrigin={ActionOrigin.ANALYSIS_TOOL}
              clientStateId={clientStateId}
              dataFrameId={item.rawData.id}
            />
          )}
        </div>
      );
    case PipelineStepDataKind.FILE:
      return (
        <div className={itemClassName}>
          <Pane title={item.rawData.name || item.rawData.filename}>
            {item.preview ? (
              <ScratchFilePaneContent scratchFile={item.rawData} />
            ) : (
              <QueryingScratchFilePaneContent fileId={item.rawData.id} />
            )}
          </Pane>
        </div>
      );
    case PipelineStepDataKind.PLATE:
      return (
        <div className={itemClassName}>
          <ScratchPlateViewerPane scratchPlateId={item.rawData.id} />
        </div>
      );
    default:
      return assertNever(item);
  }
}
